import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { ExternalLink } from "react-feather";
import Select, { Option } from "@cs/components/src/Select";
import Button from "@cs/components/src/Button";
import Input from "@cs/components/src/Input";
import Banner from "@cs/components/src/Banner";
import Section from "@cs/components/src/Section";
import ApiService from "../../services/ApiService";
import {
  Creds,
  getLaunchCreds,
  saveLaunchCreds,
  clearLaunchCreds,
} from "../../services/launchCreds";
import {
  SAFARI_BRIDGE_RESPONSE_TYPE,
  buildBridgeMessage,
  postMessageBridge,
} from "../../services/extensionBridge";

export type { Creds };
export { clearLaunchCreds };
// Re-export bridge primitives so existing imports and tests keep working.
export { SAFARI_BRIDGE_RESPONSE_TYPE, buildBridgeMessage, postMessageBridge };

const EXTENSION_ID = process.env.REACT_APP_ICONIK_EXTENSION_ID;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 10px;
`;

const SelectWrapper = styled.div`
  min-width: 250px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

export interface LaunchData {
  token: string;
  appId: string;
  systemDomainId: string;
  domainName: string;
  baseUrl: string;
  userId?: string;
  /**
   * The list of system-domain IDs the user can access on this cluster, as returned by
   * iconik's multidomainLogin.  Used by the cluster-token cache to check membership
   * before reusing a cached token for a different same-cluster org.
   * Empty array means no access restriction data available.
   */
  systemDomains?: string[];
}

/**
 * Maps each cluster's baseUrl to its home system-domain ID — the org a user's account
 * lives in on that cluster.  Mirrors the backend CLUSTER_HOME constant in organizationConfig.ts;
 * keep both in sync if a new cluster is ever added.
 *
 * iconik's multidomainLogin intentionally excludes the home domain from its system_domains
 * list (you're already in it).  The membership guard in canReuseClusterToken uses this map
 * to detect when the target IS the home domain so it can bypass that exclusion — the same
 * logic as the backend's loggedIntoTarget guard.
 */
export const CLUSTER_HOME: Record<string, string> = {
  "app.iconik.io": "1cb95046-5f76-11e8-b6d0-0a580a3c103d", // Cinesys (SI)
  "app.aws.iconik.io": "acd92bd0-3570-11ef-8710-a67e445c27bb", // Cinesys
};

/**
 * A cached cluster token entry.  Keyed by baseUrl ("app.iconik.io" | "app.aws.iconik.io").
 * The token is cluster-scoped (no system_domain_id in its JWT claims), so the same token
 * authorises any system domain within the cluster the user belongs to.
 */
export interface ClusterTokenCache {
  token: string;
  appId: string;
  baseUrl: string;
  /** User ID returned by multidomainLogin — forwarded to the extension for localStorage["userID"]. */
  userId?: string;
  systemDomains: string[];
  /** Unix seconds — copied from the backend-decoded exp so we can evict near-expired entries. */
  exp: number;
}

/**
 * Decode the `exp` field from a JWT without verifying the signature.
 * Returns 0 on any parse failure (treated as already expired → forces a fresh login).
 */
export function decodeJwtExp(token: string): number {
  try {
    const parts = token.split(".");
    if (parts.length < 2) {
      return 0;
    }
    // atob is available in browsers; Buffer.from is available in Node (test env).
    const payloadB64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const json =
      typeof atob !== "undefined"
        ? atob(payloadB64)
        : Buffer.from(payloadB64, "base64").toString("utf8");
    const payload = JSON.parse(json);
    return typeof payload.exp === "number" ? payload.exp : 0;
  } catch {
    return 0;
  }
}

/**
 * Check whether a cached cluster token covers a launch into `targetSystemDomainId`.
 *
 * Rules:
 *  1. Cross-cluster guard: cached.baseUrl must equal targetBaseUrl.
 *  2. Expiry guard: the token must not be within TOKEN_EXPIRY_BUFFER_SECONDS of expiry.
 *  3. Membership guard: if systemDomains is non-empty, the target must appear in the list
 *     OR be the cluster-home domain for targetBaseUrl.
 *
 *     The home-domain bypass mirrors the backend's loggedIntoTarget guard: iconik intentionally
 *     omits the home domain from multidomainLogin's system_domains list because you're already
 *     authenticated into it.  Without this bypass, a second launch into the cluster-home org
 *     (e.g. Cinesys SI = 1cb95046-…) would always fail the membership check even though the
 *     cached token is perfectly valid for it, defeating Fix B for the primary org.
 *
 * Returns true when all conditions pass and the cached token may be reused.
 *
 * Exported for unit testing.
 */
export const TOKEN_EXPIRY_BUFFER_SECONDS = 60;

export function canReuseClusterToken(
  cached: ClusterTokenCache,
  targetSystemDomainId: string,
  targetBaseUrl: string,
  nowSeconds = Math.floor(Date.now() / 1000)
): boolean {
  // Cross-cluster guard: never reuse a token across clusters.
  if (cached.baseUrl !== targetBaseUrl) {
    return false;
  }

  // Expiry guard: reject if the token has already expired or is about to.
  // exp === 0 means decodeJwtExp failed to parse — treat as already expired
  // (forces a fresh login) rather than as non-expiring.
  if (
    cached.exp === 0 ||
    nowSeconds >= cached.exp - TOKEN_EXPIRY_BUFFER_SECONDS
  ) {
    return false;
  }

  // Membership guard: if the cached session has domain info, the target must be in the list.
  // Exception: the cluster-home domain is intentionally absent from system_domains (iconik
  // excludes it because the user is already authenticated into it).  Treat it as reusable
  // regardless — matching the backend's loggedIntoTarget bypass in organizationConfig.ts.
  //
  // NOTE: systemDomains.length === 0 means "no restriction" here — this relies on the iconik
  // API contract that an empty system_domains list means cross-domain switching is unavailable
  // for this user (not that they have zero access), so we do not apply the membership filter.
  const isHomeDomain = CLUSTER_HOME[targetBaseUrl] === targetSystemDomainId;
  if (
    !isHomeDomain &&
    cached.systemDomains.length > 0 &&
    !cached.systemDomains.includes(targetSystemDomainId)
  ) {
    return false;
  }

  return true;
}

/**
 * Structured MFA-required response the backend returns when iconik signals that OTP
 * fields are needed for the cross-domain login.  The backend returns HTTP 200 with
 * this shape instead of a 4xx so the frontend can cleanly advance to step 2.
 */
interface MfaRequiredData {
  mfaRequired: true;
  /** "TOTP" or "MAIL_2SV" — always UPPERCASE, as iconik requires */
  otpType: "TOTP" | "MAIL_2SV";
}

// Internal alias for use within this file.
const clearCreds = clearLaunchCreds;

const chromeRuntime = () => (window as any).chrome?.runtime;

// ---------------------------------------------------------------------------
// Safari postMessage bridge
//
// Safari Web Extensions do NOT support externally_connectable, so the Chrome
// path (chrome.runtime.sendMessage(EXTENSION_ID, ...)) does not work in Safari.
// Instead, a content script injected by the extension listens for postMessage
// events on the manager page, validates the origin, and relays to the service
// worker via the internal browser.runtime.sendMessage API.
//
// Detection: after posting PING, we listen for ICONIK_BRIDGE_RESPONSE with
// requestType:"PING" within a short timeout window.  If we get it, the Safari
// bridge is active.
//
// The bridge primitives (SAFARI_BRIDGE_RESPONSE_TYPE, buildBridgeMessage,
// postMessageBridge) live in ../../services/extensionBridge and are re-exported
// from this file for backward compatibility.
// ---------------------------------------------------------------------------

/**
 * Probe the Safari extension bridge by posting a PING and waiting up to
 * `timeoutMs` milliseconds for the ICONIK_BRIDGE_RESPONSE reply.
 *
 * Returns a Promise<boolean> — true if the bridge replies, false on timeout or
 * if the environment doesn't support postMessage.
 *
 * Exported for unit testing.
 */
export function pingBridge(timeoutMs = 500): Promise<boolean> {
  return new Promise((resolve) => {
    if (
      typeof window === "undefined" ||
      typeof window.postMessage !== "function"
    ) {
      resolve(false);
      return;
    }

    let settled = false;

    const settle = (result: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      window.removeEventListener("message", handler);
      resolve(result);
    };

    const handler = (event: MessageEvent) => {
      // Validate origin: must be same origin (the bridge posts back to
      // event.origin which is window.location.origin).
      if (event.origin !== window.location.origin) {
        return;
      }
      const data = event.data;
      if (
        data &&
        data.type === SAFARI_BRIDGE_RESPONSE_TYPE &&
        data.requestType === "PING" &&
        data.ok === true
      ) {
        settle(true);
      }
    };

    window.addEventListener("message", handler);
    postMessageBridge(buildBridgeMessage("PING"));

    // Timeout: if no response arrives, the extension bridge is not active.
    setTimeout(() => settle(false), timeoutMs);
  });
}

/**
 * Load cached launch creds, validating that both:
 *   1. There is a live manager session (the cached ["session"] query result — see below).
 *   2. The in-memory cache's own expiresAt has not passed (getLaunchCreds' own check).
 *
 * Returns null AND clears the cache if either check fails — creds must never
 * outlive the session they were cached in.
 *
 * Exported for unit testing.
 */
export const loadCreds = (): Creds | null => {
  const creds = getLaunchCreds();
  if (!creds) {
    return null;
  }

  // Guard: reject if there is no valid manager session. The session itself lives in an
  // httpOnly cookie we can't read directly — getCachedSession() reads the ["session"] query's
  // last resolved value instead. Safe here: this file only renders inside AppRouter's
  // authenticated route tree, which doesn't mount until that query has already resolved once.
  if (!ApiService.getCachedSession()) {
    clearCreds();
    return null;
  }

  return creds;
};

/**
 * Cache launch creds in memory, tied to the current manager session's expiry.
 * If no live session can be found, falls back to now + 1 hour so the cached
 * creds are still bounded (and will be rejected by loadCreds's session check
 * on the next load anyway).
 *
 * Exported for unit testing.
 */
export const saveCreds = (creds: Creds) => {
  const session = ApiService.getCachedSession();
  // Prefer the session's own expiry so creds max-age === session max-age.
  // Fall back to now + 1h if the session can't be read (shouldn't happen in
  // practice — we're always called right after a successful authenticated API
  // call — but guards against an edge case rather than storing an unbounded value).
  const expiresAt = session?.expires ?? Math.floor(Date.now() / 1000) + 3600;
  saveLaunchCreds(creds, expiresAt);
};

/**
 * Build the POST body for /launch.
 *
 * Phase 1 (no OTP): omit otp_type and otp entirely.
 * Phase 2 (OTP known): include otp_type (UPPERCASE, from the method the backend
 * returned) and otp (whitespace-stripped).
 *
 * This function is exported for unit testing.
 */
export function buildLaunchBody(
  creds: Creds,
  otp: string,
  otpType?: string
): Record<string, string> {
  const cleaned = otp.replace(/\s/g, "");
  const body: Record<string, string> = {
    email: creds.email,
    password: creds.password,
  };
  if (cleaned) {
    // otp_type must be UPPERCASE — iconik rejects lowercase "totp" with 401.
    body.otp_type = (otpType ?? "TOTP").toUpperCase();
    body.otp = cleaned;
  }
  return body;
}

/**
 * Human-readable prompt copy for each OTP method.
 */
function otpLabel(otpType: "TOTP" | "MAIL_2SV"): string {
  if (otpType === "MAIL_2SV") {
    return "Enter the code we emailed you";
  }
  return "Enter the code from your authenticator app";
}

const SelectOrganization = () => {
  const { systemDomainId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [extensionReady, setExtensionReady] = useState(false);
  const [launching, setLaunching] = useState(false);

  /**
   * Modal step:
   *   null       = closed
   *   "creds"    = step 1: email + password form
   *   "otp"      = step 2: code-only form
   */
  const [modalStep, setModalStep] = useState<null | "creds" | "otp">(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [otp, setOtp] = useState("");
  /**
   * The OTP method returned by the backend in Phase 1 ("TOTP" | "MAIL_2SV").
   * Set when we advance to step 2; cleared on modal close.
   */
  const [otpType, setOtpType] = useState<"TOTP" | "MAIL_2SV">("TOTP");

  // Pending creds saved during Phase 1 so Phase 2 can reuse them.
  const pendingCredsRef = useRef<Creds | null>(null);

  /**
   * In-memory cluster token cache — keyed by baseUrl (cluster identifier).
   * Lives for the lifetime of this component instance (i.e. the manager session).
   * Not persisted to sessionStorage: an iconik token refresh requires a new page load anyway,
   * and keeping the cache in-memory avoids token leakage across separate manager sessions.
   */
  const clusterCacheRef = useRef<Map<string, ClusterTokenCache>>(new Map());

  const pingRef = useRef(false);

  useEffect(() => {
    if (pingRef.current) {
      return;
    }
    pingRef.current = true;

    // Path 1 — Chrome: use chrome.runtime.sendMessage via externally_connectable.
    // The EXTENSION_ID env var is only set in Chrome deployments.
    const rt = chromeRuntime();
    if (EXTENSION_ID && rt) {
      rt.sendMessage(EXTENSION_ID, { type: "PING" }, (response: any) => {
        if (!rt.lastError && response?.ok) {
          setExtensionReady(true);
        }
      });
      return;
    }

    // B1: if chrome.runtime is present (Chrome browser), neither path is viable
    // (EXTENSION_ID is not set).  Skip the Safari probe entirely — no point firing
    // a 500 ms pingBridge timeout on every mount in a Chrome deployment that has no
    // extension configured.
    if (rt) {
      return;
    }

    // Path 2 — Safari: probe the content-script postMessage bridge.
    // Only reached when chrome.runtime is absent (non-Chrome browser).
    // No EXTENSION_ID needed; the content script is always injected by the extension
    // on matching origins.  pingBridge posts a PING and waits for ICONIK_BRIDGE_RESPONSE.
    pingBridge().then((ready) => {
      if (ready) {
        setExtensionReady(true);
      }
    });
  }, []);

  const closeModal = () => {
    setModalStep(null);
    setOtp("");
    setOtpType("TOTP");
    pendingCredsRef.current = null;
  };

  /**
   * Fire the iconik extension to open the target org.
   *
   * Feature-detects which path to use:
   *   Chrome: chrome.runtime.sendMessage via externally_connectable.
   *   Safari: window.postMessage to the content-script relay bridge.
   *
   * Both paths are attempted when both environments coexist (e.g. in dev).
   * In practice only one will be active at a time.
   */
  const fireExtension = (launchData: LaunchData) => {
    // Build the message once so both paths are guaranteed identical payloads.
    const msg = buildBridgeMessage(
      "ICONIK_LAUNCH",
      launchData as unknown as Record<string, unknown>
    );

    // Chrome path — uses externally_connectable.
    const rt = chromeRuntime();
    if (EXTENSION_ID && rt) {
      rt.sendMessage(EXTENSION_ID, msg);
      return;
    }

    // Safari path — relay via content-script postMessage bridge.
    postMessageBridge(msg);
  };

  /**
   * Populate the cluster cache after a successful launch.
   * domainName is intentionally NOT stored: it belongs to the org that triggered the
   * initial MFA flow, but cache hits are for DIFFERENT orgs on the same cluster.  The
   * target org's title is fetched fresh from the GET in tryLaunchFromCache instead.
   */
  const populateClusterCache = (launchData: LaunchData) => {
    const { baseUrl, token, appId, userId, systemDomains } = launchData;
    const exp = decodeJwtExp(token);
    clusterCacheRef.current.set(baseUrl, {
      token,
      appId,
      baseUrl,
      userId,
      systemDomains: systemDomains ?? [],
      exp,
    });
  };

  /**
   * Phase 1: submit email + password with no OTP.
   *   - Backend returns launch creds → populate cluster cache, fire extension, done.
   *   - Backend returns { mfaRequired: true, otpType } → advance to step 2.
   *   - Backend returns auth error → clear saved creds, show error.
   */
  const launchPhase1 = async (creds: Creds) => {
    // C1: guard on systemDomainId only — NOT EXTENSION_ID.
    // EXTENSION_ID is unset on Safari; Safari uses the postMessage bridge instead.
    if (!systemDomainId) {
      return;
    }
    setLaunching(true);
    setError(null);
    try {
      const body = buildLaunchBody(creds, "");

      const { data } = await ApiService.fetch<{
        data: LaunchData | MfaRequiredData;
      }>("POST", `/api/config/organization/${systemDomainId}/launch`, body);

      if ((data as MfaRequiredData).mfaRequired) {
        // MFA required — advance to step 2 with the method the backend resolved.
        const mfa = data as MfaRequiredData;
        pendingCredsRef.current = creds;
        setOtp("");
        setOtpType(mfa.otpType);
        setModalStep("otp");
        return;
      }

      // Non-MFA success — cache the cluster token for future same-cluster launches.
      const launchData = data as LaunchData;
      populateClusterCache(launchData);
      saveCreds(creds);
      closeModal();
      fireExtension(launchData);
    } catch (e: any) {
      if (e.message === "Invalid iconik credentials") {
        clearCreds();
        // Open the modal so the error banner is visible — without this, the error
        // is set but modalStep is null so the Banner never renders.
        setModalStep("creds");
        setError("Wrong email or password.");
      } else {
        // Open the modal so the error banner is visible.
        setModalStep("creds");
        setError(e.message ?? "Something went wrong. Try again.");
      }
    } finally {
      setLaunching(false);
    }
  };

  /**
   * Phase 2: submit the OTP code together with the saved creds.
   */
  const launchPhase2 = async () => {
    const creds = pendingCredsRef.current;
    // C1: guard on systemDomainId only — NOT EXTENSION_ID.
    // EXTENSION_ID is unset on Safari; Safari uses the postMessage bridge instead.
    if (!creds || !otp.trim() || !systemDomainId) {
      return;
    }
    setLaunching(true);
    setError(null);
    try {
      const body = buildLaunchBody(creds, otp, otpType);

      const { data } = await ApiService.fetch<{
        data: LaunchData | MfaRequiredData;
      }>("POST", `/api/config/organization/${systemDomainId}/launch`, body);

      // Guard: phase 2 should always return launch creds, never mfaRequired.
      // If it does (e.g. an unexpected backend response), stay on the code step.
      if ((data as MfaRequiredData).mfaRequired) {
        setError("Something went wrong. Please try again.");
        return;
      }

      // Phase 2 MFA success — cache the cluster token for future same-cluster launches.
      const launchData = data as LaunchData;
      populateClusterCache(launchData);
      saveCreds(creds);
      closeModal();
      fireExtension(launchData);
    } catch (e: any) {
      if (
        e.message?.startsWith("iconik login failed:") ||
        e.message?.includes("otp") ||
        e.message?.includes("OTP")
      ) {
        // OTP was wrong or expired — clear the field so the user types a fresh code.
        // Saved creds are kept; no need to re-enter email/password.
        setOtp("");
        setError("Invalid or expired code — enter a new one.");
      } else if (e.message === "Invalid iconik credentials") {
        // Saved creds went stale between phase 1 and phase 2 — go back to step 1.
        // (Should be rare now that phase-2 401/404 is treated as an OTP error.)
        clearCreds();
        pendingCredsRef.current = null;
        setOtp("");
        setModalStep("creds");
        setError("Session expired — please sign in again.");
      } else {
        setError(e.message ?? "Something went wrong. Try again.");
      }
    } finally {
      setLaunching(false);
    }
  };

  /**
   * Try to launch using a cached cluster token instead of the full MFA flow.
   *
   * Looks up the org's baseUrl via a lightweight GET, then checks the in-memory
   * cluster cache.  If the cached token passes all guards (not expired, membership
   * satisfied), fires the extension immediately.  Otherwise falls through to the
   * caller so it can run the full MFA flow.
   *
   * Returns true if the launch was handled via cache (caller should NOT proceed to
   * phase 1); false if the cache missed and the caller should run the full flow.
   */
  const tryLaunchFromCache = async (): Promise<boolean> => {
    // C1: guard on systemDomainId only — NOT EXTENSION_ID.
    // EXTENSION_ID is unset on Safari; Safari uses the postMessage bridge instead.
    if (!systemDomainId) {
      return false;
    }
    // Check if we have any cached entries at all before making a network call.
    if (clusterCacheRef.current.size === 0) {
      return false;
    }
    try {
      // Fetch the org info — a cheap authenticated call to our own backend.
      // We need baseUrl to look up the cluster cache, and title for the domainName
      // the extension writes to localStorage (must be the TARGET org's title, not the
      // populating org's — those are different on every cache-hit benefit case).
      const { data: orgInfo } = await ApiService.fetch<{
        data: { baseUrl: string; title: string };
      }>("GET", `/api/config/organization/${systemDomainId}`);

      const cachedEntry = clusterCacheRef.current.get(orgInfo.baseUrl);
      if (!cachedEntry) {
        // No cached token for this cluster.
        return false;
      }

      if (!canReuseClusterToken(cachedEntry, systemDomainId, orgInfo.baseUrl)) {
        // Cache miss (expired or not a member) — evict the stale entry and fall through.
        clusterCacheRef.current.delete(orgInfo.baseUrl);
        return false;
      }

      // Before using the cached token, ask the extension whether any open iconik tab
      // has a localStorage token.  If a tab exists but has no token (hasSession: false),
      // the user signed out of iconik directly — our cached token is almost certainly
      // revoked.  Evict it and fall through to full re-auth for a fresh token.
      const rt = chromeRuntime();
      let hasSession: boolean | null = null;
      if (EXTENSION_ID && rt) {
        // Chrome: use chrome.runtime.sendMessage with callback.
        hasSession = await new Promise<boolean | null>((resolve) => {
          rt.sendMessage(
            EXTENSION_ID,
            { type: "ICONIK_HAS_SESSION" },
            (resp: any) => {
              resolve(rt.lastError ? null : resp?.hasSession ?? null);
            }
          );
        });
      } else if (!rt) {
        // Safari: use the postMessage bridge; wait up to 500 ms for the response.
        hasSession = await new Promise<boolean | null>((resolve) => {
          let settled = false;
          const settle = (result: boolean | null) => {
            if (settled) {
              return;
            }
            settled = true;
            window.removeEventListener("message", handler);
            resolve(result);
          };
          const handler = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) {
              return;
            }
            const d = event.data;
            if (
              d?.type === SAFARI_BRIDGE_RESPONSE_TYPE &&
              d?.requestType === "ICONIK_HAS_SESSION"
            ) {
              settle(d.hasSession ?? null);
            }
          };
          window.addEventListener("message", handler);
          postMessageBridge(buildBridgeMessage("ICONIK_HAS_SESSION"));
          setTimeout(() => settle(null), 500);
        });
      }
      // hasSession === false: tab open, no token — user signed out of iconik directly.
      // hasSession === null: no open iconik tab — can't verify the cached token is still
      //   valid.  Without a live tab to probe, we can't distinguish a valid closed-tab
      //   from a post-logout closed-tab, so evict and force re-auth to get a fresh token.
      //   Both Chrome and Safari now inject fresh tokens early enough (before SPA reads
      //   localStorage) so MFA re-auth correctly lands the user into iconik.
      if (hasSession !== true) {
        clusterCacheRef.current.delete(orgInfo.baseUrl);
        return false;
      }

      // Cache hit — open the org with the cached cluster token + the new systemDomainId.
      // domainName comes from orgInfo.title (the TARGET org's friendly name, fetched above)
      // so the extension writes the correct title to localStorage — not the name of whatever
      // org originally populated this cache entry.
      // userId comes from the cached entry (the manager-session user, constant across orgs).
      fireExtension({
        token: cachedEntry.token,
        appId: cachedEntry.appId,
        baseUrl: cachedEntry.baseUrl,
        systemDomainId,
        domainName: orgInfo.title,
        userId: cachedEntry.userId,
        systemDomains: cachedEntry.systemDomains,
      });
      return true;
    } catch {
      // On any error (e.g. network, 404), fall through to the full MFA flow.
      return false;
    }
  };

  const handleLaunchClick = async () => {
    setLaunching(true);

    // Fast path: if we already have a cached cluster token that covers this org,
    // open it immediately without password/MFA prompts.
    const handled = await tryLaunchFromCache();
    if (handled) {
      // Cache hit — extension fired; clear the spinner and we're done.
      setLaunching(false);
      return;
    }

    // No cache hit — fall through to the full MFA flow.
    // launchPhase1 sets/clears its own launching state, so clear ours first.
    setLaunching(false);

    const creds = loadCreds();
    if (creds) {
      // Saved creds exist — probe Phase 1 immediately (no modal yet).
      // If Phase 1 returns mfaRequired, the modal opens directly on step 2.
      // If Phase 1 fails auth (stale creds), launchPhase1's catch clears them
      // and shows an error; the modal is opened on step 1 below on next click.
      launchPhase1(creds);
    } else {
      setEmail("");
      setPassword("");
      setOtp("");
      setOtpType("TOTP");
      pendingCredsRef.current = null;
      setError(null);
      setModalStep("creds");
    }
  };

  const handleSubmitCredentials = () => {
    if (email && password) {
      launchPhase1({ email, password });
    }
  };

  return (
    <Wrapper>
      <SelectWrapper>
        <Select
          placeholder="Select an organization"
          value={systemDomainId}
          onChange={({ value }) => {
            navigate(
              location.pathname
                .replace(/dashboard\/[^/]*\//, `dashboard/${value}/`)
                .replace(/plugin\/.*/, "plugin")
            );
          }}
          optionsAsync={{
            key: `_/organization`,
            fn: async () =>
              (
                await ApiService.fetch<{ data: Option[] }>(
                  "GET",
                  `/api/select_option/_/organization`
                )
              ).data,
          }}
        ></Select>
      </SelectWrapper>

      {extensionReady && systemDomainId && (
        <Button
          icon={ExternalLink}
          label="Open in Iconik"
          type="default"
          onClick={handleLaunchClick}
          loading={launching}
          disabled={launching}
        />
      )}

      {modalStep !== null && (
        <ModalOverlay>
          {/*
           * No ClickOutside wrapper here — autofill extensions (1Password, etc.)
           * steal window focus and fire mousedown events outside the modal when
           * their dropdown opens. ClickOutside would misinterpret that as a
           * "click outside" and dismiss the modal.  Section handles Escape-key
           * and backdrop-click (mousedown+mouseup same target) correctly without
           * ClickOutside.
           */}
          <Section
            modal
            title="Sign in to Iconik"
            onClose={closeModal}
            width="480px"
            pad
            footer={
              <ModalButtons>
                <Button
                  label="Cancel"
                  type="danger"
                  onClick={closeModal}
                  disabled={launching}
                />
                {modalStep === "creds" ? (
                  <Button
                    label="Continue"
                    type="success"
                    icon={ExternalLink}
                    onClick={handleSubmitCredentials}
                    loading={launching}
                    disabled={!email || !password || launching}
                  />
                ) : (
                  <Button
                    label="Verify"
                    type="success"
                    onClick={launchPhase2}
                    loading={launching}
                    disabled={!otp.trim() || launching}
                  />
                )}
              </ModalButtons>
            }
          >
            {modalStep === "creds" ? (
              <>
                <Input
                  label="Email"
                  value={email}
                  onChange={setEmail}
                  onSubmit={handleSubmitCredentials}
                  autoComplete="email"
                  name="email"
                  id="iconik-launch-email"
                />
                <Input
                  label="Password"
                  type="password"
                  value={password}
                  onChange={setPassword}
                  onSubmit={handleSubmitCredentials}
                  autoComplete="current-password"
                  name="password"
                  id="iconik-launch-password"
                />
              </>
            ) : (
              <>
                <p
                  style={{
                    marginTop: 0,
                    marginBottom: 16,
                    color: "var(--gray300)",
                  }}
                >
                  {otpLabel(otpType)}
                </p>
                <Input
                  label="Verification code"
                  placeholder="6-digit code"
                  value={otp}
                  onChange={setOtp}
                  onSubmit={launchPhase2}
                  autoFocus
                  autoComplete="one-time-code"
                  name="one-time-code"
                  id="iconik-launch-otp"
                />
              </>
            )}
            {error && <Banner error={error} />}
          </Section>
        </ModalOverlay>
      )}
    </Wrapper>
  );
};

export default SelectOrganization;
